import React, { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import http from "@shared/api/http";
import "@/assets/styles/Utils.css";
import { useTranslation } from "react-i18next";
import { Collapse } from "react-bootstrap";
import parse from "html-react-parser";
import QuestionnaireDecoder from "@shared/components/QuestionnaireDecoder";
import { createProgramFeedbackNotification, createProgramCompletedNotification } from "@features/programs/services/notificationService";
import SetsRepsWeightInput from "@shared/components/SetsRepsWeightInput";
import { FeedbackForm } from "./FeedbackForm";
import { FeedbackReward, SessionComplete } from "@features/programs/components/gamification";
import { SharedVideoPlayer } from "@sophy/shared-ui";
import {
  parseExerciseSteps,
  getPrescriptionItems,
  getPlayerLabels,
} from "@features/programs/utils/exerciseCardData";
import "./SlideshowModal.css";

export function SlideshowModal(props) {
  const [open, setOpen] = useState(false);
  const [feedbackLvl, setLvl] = useState(5);
  const [feedbackText, setFeedbackText] = useState("");
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState({});
  const [exerciseLogs, setExerciseLogs] = useState({});
  const { t, i18n } = useTranslation(); //eslint-disable-line no-unused-vars

  // Check if current slide is the feedback slide (last slide)
  const isFeedbackSlide = props.currentIndex === props.programLength;

  const currentExercise = props.exercises?.props?.exercise;
  const slideSource = currentExercise?.source || {};
  const slideVideoSrc = slideSource.video_url || null;
  const slidePosterSrc = slideSource.thumb_url || null;
  const slidePrescription = getPrescriptionItems(currentExercise?.prescription, t);
  const slideSteps = parseExerciseSteps(
    slideSource.mapping_sources_translation?.[0]?.steps || slideSource.steps
  );

  // One row per prescribed set, pre-filled with the prescribed reps/weight.
  const defaultSetsFor = (exercise) => {
    const count = Math.max(1, parseInt(exercise?.series, 10) || 1);
    const reps = exercise?.repetitions ? String(exercise.repetitions) : "";
    const weight = Number(exercise?.weight) > 0 ? String(exercise.weight) : "";
    return Array.from({ length: count }, () => ({
      reps,
      weight,
      _uid: Math.random().toString(36).slice(2),
    }));
  };

  // Payload for the API: keep each set's number, drop empty sets and exercises.
  const buildExercisesPayload = () =>
    Object.entries(exerciseLogs)
      .map(([exerciseId, sets]) => ({
        exercise_id: Number(exerciseId),
        sets: (sets || [])
          .map((s, i) => ({ set: i + 1, reps: s.reps, weight: s.weight }))
          .filter(
            (s) =>
              String(s.reps ?? "").trim() !== "" ||
              String(s.weight ?? "").trim() !== ""
          ),
      }))
      .filter((e) => e.sets.length > 0);

  // Check if we have a questionnaire template
  const hasQuestionnaire = props.questionnaireTemplate?.content?.length > 0;

  // Initialize questionnaire answers with default values when questionnaire is loaded
  useEffect(() => {
    if (hasQuestionnaire && props.questionnaireTemplate?.content) {
      const defaultAnswers = {};
      props.questionnaireTemplate.content.forEach((field, index) => {
        if (field.type === "scale") {
          // Initialize scale fields with their minimum value
          defaultAnswers[index] = field.min || 0;
        }
        // Other field types will initialize as undefined and get handled properly
      });
      setQuestionnaireAnswers(defaultAnswers);
    }
  }, [hasQuestionnaire, props.questionnaireTemplate]);

  // Seed each exercise's log with prescribed defaults the first time it is shown
  // (also re-seeds on reopen, since the close handler clears the logs).
  useEffect(() => {
    if (props.show && currentExercise) {
      setExerciseLogs((prev) =>
        prev[currentExercise.id]
          ? prev
          : { ...prev, [currentExercise.id]: defaultSetsFor(currentExercise) }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show, currentExercise?.id]);

  // Reset per-run state when the slideshow closes.
  useEffect(() => {
    if (!props.show) {
      setExerciseLogs({});
      setLvl(5);
      setFeedbackText("");
    }
  }, [props.show]);

  const handleFeedbackSubmit = async () => {
    if (hasQuestionnaire) {
      // Submit questionnaire responses
      try {
        const url_q = `/:locale/v2/:token/programs/${props.programId}/questionnaire_responses.json`;
        const payload = {
          questionnaire_template_id: props.questionnaireTemplate.id,
          responses: questionnaireAnswers,
          // Optional placeholders; server will use defaults if omitted
          pain_level: feedbackLvl,
          program_feedback: feedbackText,
          // Per-set exercise feedback (reps/weight per set)
          exercises: buildExercisesPayload(),
        };
        await http.post(url_q, payload, { headers: { "Content-Type": "application/json" } });

        // Create notifications for questionnaire feedback
        if (feedbackText && feedbackText.trim()) {
          await createProgramFeedbackNotification(props.programId, props.programName, feedbackText);
        }
        await createProgramCompletedNotification(props.programId, props.programName);

        // The session is saved: the parent switches this modal to the
        // completion view and closes it from there.
        if (props.onSessionSaved) {
          props.onSessionSaved({ pain_level: Number(feedbackLvl), program_feedback: feedbackText });
          return;
        }
        alert(t("feedback.feedback_sent_successfully"));
      } catch (error) {
        console.error("Error submitting questionnaire:", error);
        alert(t("feedback.feedback_error"));
      }
    } else {
      // Default feedback
      if (props.onSubmitFeedback) {
        const result = await props.onSubmitFeedback(feedbackLvl, feedbackText, buildExercisesPayload());
        if (result === "completed") return;
      }
    }
    props.onHide();
  };

  const handleNext = () => {
    if (isFeedbackSlide) {
      // If on feedback slide, submit feedback and close
      handleFeedbackSubmit();
    } else {
      props.onNext();
    }
  };

  const handlePrevious = () => {
    if (isFeedbackSlide) {
      // If on feedback slide, go back to last exercise
      props.onPrevious();
    } else {
      props.onPrevious();
    }
  };

  const {
    completion,
    onSessionSaved,
    program,
    programs,
    onSubmitFeedback,
    questionnaireTemplate,
    exercises,
    currentIndex,
    programLength,
    programId,
    programName,
    onNext,
    onPrevious,
    ...modalProps
  } = props;

  return (
    <Modal
      {...modalProps}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          {t("program.slideshowTitle")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {completion ? (
          <SessionComplete
            outcome={completion}
            programName={programName}
            onDone={props.onHide}
          />
        ) : isFeedbackSlide ? (
          // Feedback slide content
          <div style={{ textAlign: "center" }}>
            <h4>{hasQuestionnaire ? props.questionnaireTemplate.name : t("feedback.send-therapist")}</h4>
            {hasQuestionnaire ? (
              <QuestionnaireDecoder
                content={props.questionnaireTemplate.content}
                readOnly={false}
                answers={questionnaireAnswers}
                onChange={(idx, value) => {
                  setQuestionnaireAnswers((prev) => {
                    const next = { ...prev };
                    if (typeof value === "object" && value.option) {
                      // multi-select
                      const arr = Array.isArray(next[idx]) ? next[idx] : [];
                      if (value.checked) {
                        next[idx] = [...arr, value.option];
                      } else {
                        next[idx] = arr.filter((v) => v !== value.option);
                      }
                    } else {
                      next[idx] = value;
                    }
                    return next;
                  });
                }}
              />
            ) : (
              <>
                <FeedbackForm
                  feedbackLvl={feedbackLvl}
                  setLvl={setLvl}
                  feedbackText={feedbackText}
                  setFeedbackText={setFeedbackText}
                  onSubmit={handleFeedbackSubmit}
                  showTitle={false}
                  showSubmit={false}
                />
                {program && (
                  <div style={{ maxWidth: "460px", margin: "18px auto 0" }}>
                    <FeedbackReward
                      program={program}
                      programs={programs}
                      pendingValue={feedbackLvl}
                      pendingFeedback={feedbackText}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          // Exercise slide content
          <>
            {/* Same player and the same labelled prescription as the cards —
                the slideshow used the browser's control bar and the raw
                "12 | 60 | 3" string. */}
            <div className="slideshow-exercise">
              <h4 className="slideshow-title">
                {props.exercises
                  ? parse(
                      `${props.exercises.props.exercise?.name || ""} (${
                        props.currentIndex + 1
                      }/${props.programLength})`
                    )
                  : ""}
              </h4>

              {slideVideoSrc ? (
                <SharedVideoPlayer
                  key={slideVideoSrc}
                  videoSrc={slideVideoSrc}
                  posterSrc={slidePosterSrc}
                  preload="metadata"
                  /* Understood from shared-ui 1.2.3 on; older versions ignore
                     it and simply wait for a tap. */
                  autoPlay={!isFeedbackSlide}
                  labels={getPlayerLabels(t)}
                  className="slideshow-player"
                />
              ) : (
                slidePosterSrc && (
                  <img
                    className="slideshow-still"
                    src={slidePosterSrc}
                    alt=""
                  />
                )
              )}

              {slidePrescription ? (
                <dl className="sophy-xc-rx slideshow-rx">
                  {slidePrescription.map((item, index) => (
                    <div key={index}>
                      <dt>{item.label}</dt>
                      <dd>
                        {item.value}
                        {item.unit ? <small>{item.unit}</small> : null}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <div className="slideshow-rx-raw">
                  {props.exercises
                    ? props.exercises.props.exercise.prescription
                    : ""}
                </div>
              )}
            </div>
            <div style={{ maxWidth: "320px", margin: "0 auto" }}>
              <button
                className="description-toggle"
                type="button"
                onClick={() => setOpen(!open)}
                aria-controls="collapse-description"
                aria-expanded={open}
              >
                <span>{t("feedback.description", { defaultValue: "Beschreibung" })}</span>
                <span aria-hidden="true" style={{ fontSize: "20px", lineHeight: 1 }}>
                  {open ? "▴" : "▾"}
                </span>
              </button>
              <Collapse in={open}>
                <div id="collapse-description">
                  <ol className="slideshow-steps">
                    {slideSteps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
              </Collapse>
            </div>
            {currentExercise && (
              <SetsRepsWeightInput
                sets={exerciseLogs[currentExercise.id] || defaultSetsFor(currentExercise)}
                onChange={(sets) =>
                  setExerciseLogs((prev) => ({ ...prev, [currentExercise.id]: sets }))
                }
                repsPlaceholder={currentExercise.repetitions}
                weightPlaceholder={
                  Number(currentExercise.weight) > 0 ? currentExercise.weight : undefined
                }
                showWeight={Number(currentExercise.weight) > 0}
              />
            )}
          </>
        )}

        {!completion && (
        <div className="d-flex justify-content-between mt-3">
          <Button
            className="bs-button-color d-inline-flex align-items-center"
            onClick={handlePrevious}
            disabled={props.currentIndex === 0 && !isFeedbackSlide}
          >
            <span className="me-2">←</span>
            {t("program.previous")}
          </Button>
          <Button
            className="bs-button-color d-inline-flex align-items-center"
            onClick={handleNext}
          >
            {isFeedbackSlide ? (
              t("actions.send")
            ) : (
              <>
                {t("program.next")}
                <span className="ms-2">→</span>
              </>
            )}
          </Button>
        </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
